import * as Sentry from '@sentry/browser';
import 'isomorphic-fetch';
import moment from 'moment';

export class Api {
  constructor(baseUrl, delay = 0) {
    this.baseUrl = baseUrl;
    this.delay = delay;
    this.token = null;
    this.lang = '';
  }

  objectToParams(obj) {
    const pairs = [];
    for (var key in obj)
      if (obj.hasOwnProperty(key))
        pairs.push(
          encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]),
        );
    return pairs.join('&');
  }

  paramsToObject(params) {
    const query = params.substr(1);
    const result = {};
    query.split('&').forEach(part => {
      if (part) {
        const item = part.split('=');
        result[item[0]] = decodeURIComponent(item[1]);
      }
    });
    return result;
  }

  get(url, params = {}) {
    let queryParams = this.objectToParams(params);
    let urlWithQueryParams =
      `${this.baseUrl}${url}` + (queryParams ? '?' + queryParams : '');
    return this.request(urlWithQueryParams);
  }

  post(url, body) {
    return this.request(`${this.baseUrl}${url}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  put(url, body) {
    return this.request(`${this.baseUrl}${url}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  delete(url, body) {
    return this.request(`${this.baseUrl}${url}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  }

  redirect(url) {
    if (typeof window !== 'undefined') {
      window.location = url;
    }
  }

  request(url, config = {}) {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(config.headers || {}),
    };

    delete config.headers;

    config = {
      headers,
      ...config,
      credentials: 'include',
    };

    return fetch(url, config)
      .then(response => {
        if (response.ok) {
          if (response.headers.get('Content-Type') === 'application/json') {
            return response.json();
          }
          return {};
        } else {
          let error = new Error(response.statusText);
          error.response = response;
          throw error;
        }
      })
      .catch(error => {
        if (error && error.response && error.response.status === 401) {
          // dont log 401
        } else {
          Sentry.captureException(error);
        }
        if (error.response) {
          return error.response
            .json()
            .then(error_details => {
              error.details = error_details;
              throw error;
            })
            .catch(e => {
              throw e;
            });
        } else {
          throw error;
        }
      });
  }

  getDeparture() {
    return this.get(`/api/stations/departure?lang=${this.lang}`)
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getReservationDetailTable(vs, pass) {
    return this.get(
      `/api/cms/reservation-detail-table?vs=${vs}&pass=${pass}&lang=${
        this.lang
      }`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getBus(fromId, toId, busId) {
    return this.get(
      `/api/seats?from_station=${fromId}&to_station=${toId}&id_bus=${busId}&lang=${
        this.lang
      }`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getToStation(id) {
    return this.get(
      `/api/stations/arrival?departure_station_id=${id}&lang=${this.lang}`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getLogin(email, password) {
    return this.post(`/club/auth/login?lang=${this.lang}`, {
      login: email,
      password,
    });
  }

  getLoggedUser() {
    return this.request(`${this.baseUrl}/club/auth/me?lang=${this.lang}`, {
      method: 'GET',
    });
  }

  getMessages() {
    return this.request(
      `${this.baseUrl}/api/search/messages?lang=${this.lang}`,
      {
        method: 'GET',
      },
    );
  }

  getPaygateMessages() {
    return this.request(
      `${this.baseUrl}/api/search/paygate-messages?lang=${this.lang}`,
      {
        method: 'GET',
      },
    );
  }

  getConnections(params) {
    if (params.ticketType === 'B') {
      params.ticketType = 'S';
    }
    return this.get(
      `/api/search?departure_station_id=${
        params.stationFrom
      }&arrival_station_id=${params.stationTo}&type=${
        params.ticketType
      }&date_from=${moment(params.startDate).format('YYYY-MM-DD')}&lang=${
        this.lang
      }`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getConnectionsR(params) {
    return this.get(
      `/api/search?departure_station_id=${
        params.stationFrom
      }&arrival_station_id=${params.stationTo}&type=${
        params.ticketType
      }&date_from=${moment(params.startDate).format(
        'YYYY-MM-DD',
      )}&date_to=${moment(params.endDate).format('YYYY-MM-DD')}&lang=${
        this.lang
      }`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getConnectionDetail(sectorId, fromStation, toStation) {
    return this.get(
      `/api/search/sector-detail?id_dps=${sectorId}&from_id_bus_stop=${fromStation}&to_id_bus_stop=${toStation}&lang=${
        this.lang
      }`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getTickets(ticketType, connectionId1, connectionId2 = undefined) {
    let url = `/api/search/tickets?connection_id1=${connectionId1}&type=${ticketType}&lang=${
      this.lang
    }`;
    url += `&connection_id2=${connectionId2}`;
    return this.get(url)
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  createReservation(data) {
    return this.post(`/api/order?lang=${this.lang}`, {
      data,
    });
  }

  selectBackWayForOpenTicket(data) {
    return this.post(`/api/user/openticketbuyback?lang=${this.lang}`, data);
  }

  registration(user) {
    return this.post(`/api/user/registration?lang=${this.lang}`, user);
  }

  getSeatingsInBuss(idDps, idPrejazd, idBus) {
    return this.get(
      `/api/user/check-free-seats?id_prejazd=${idPrejazd}&id_dps=${idDps}&id_bus=${idBus}&lang=${
        this.lang
      }`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }
  getTicketByTicketId(ticketId) {
    return this.get(`/api/user/ticket/?ticket_id=${ticketId}&lang=${this.lang}`)
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getTransactionDetail(pass, vs) {
    return this.get(
      `/api/user/transactiondetail?pass=${pass}&vs=${vs}&lang=${this.lang}`,
    )
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  getOtherContent(id) {
    return this.get(`/api/cms/other-content?id=${id}&lang=${this.lang}`)
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  repeatMail(vs, pass, mail) {
    return this.post(`/api/order/mail-repeat?lang=${this.lang}`, {
      vs,
      mail,
      pass,
    });
  }

  getLocale(lang) {
    return this.get(`/api/locale?lang=${lang}`)
      .then(data => data)
      .catch(error => {
        throw error;
      });
  }

  paymentResult(paygate, params) {
    return this.get(
      `/api/payment/${paygate}?${this.objectToParams(params)}&lang=${
        this.lang
      }`,
    );
  }

  myFavouritePassengers() {
    return this.get(`/api/user/my-favourite-passengers?lang=${this.lang}`);
  }
}

export const factory = (baseUrl, config = {}) => {
  return new Api(baseUrl, config);
};

export default factory(process.env.REACT_APP_API_BASE_URL);



// WEBPACK FOOTER //
// ./src/services/api.js